/**
 * Shared renderers for consignment-based inventory exceptions.
 *
 * Used by unsent POs, overdue POs, and pending stock transfers — all of
 * which share the ConsignmentExceptionFormatter payload.
 */
import React from 'react'
import { Launch as LaunchIcon } from '@mui/icons-material'
import { Link, Stack } from '@mui/material'
import { handleLightspeedLinkClick } from '../../../../utils/externalLinks'
import type { RenderContext } from '../../types'

export type ConsignmentItemType = 'PO' | 'Transfer'

const nonEmpty = (value: unknown): string | null => {
  if (value === null || value === undefined) return null
  const str = String(value).trim()
  return str || null
}

export const resolveConsignmentReference = (row: Record<string, any>): string | null => {
  return nonEmpty(row.reference_number) ?? nonEmpty(row.supplier_invoice_number)
}

export const consignmentReferenceSortValue = (row: Record<string, any>): string =>
  resolveConsignmentReference(row) ?? '\uFFFF'

export const createConsignmentLinkCell = (itemType: ConsignmentItemType) => {
  return (_value: any, row: Record<string, any>, _ctx: RenderContext): React.ReactNode => {
    const label = resolveConsignmentReference(row)
    if (!row.lightspeed_link) {
      return label ?? '-'
    }
    const isMissing = label === null
    const displayText = label ?? 'Open'
    const popupTitle = isMissing ? `${itemType} (no reference)` : `${itemType} ${label}`
    return React.createElement(
      Stack,
      { direction: 'row', alignItems: 'center', spacing: 1 },
      React.createElement(
        Link,
        {
          href: row.lightspeed_link,
          onClick: (e: React.MouseEvent<HTMLAnchorElement>) =>
            handleLightspeedLinkClick(e, row.lightspeed_link, popupTitle),
          title: isMissing ? 'No reference set in Lightspeed' : undefined,
          sx: {
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
            fontWeight: isMissing ? 400 : 500,
            color: isMissing ? 'text.secondary' : undefined,
            fontStyle: isMissing ? 'italic' : undefined,
          },
        },
        displayText
      ),
      React.createElement(LaunchIcon, {
        fontSize: 'small',
        sx: { color: 'text.secondary' },
      })
    )
  }
}
